"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Card, Typography, Progress, Spin, Empty, Alert, Button } from "antd"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from "recharts"
import { LeftOutlined, RightOutlined, TrophyOutlined, ReloadOutlined } from "@ant-design/icons"
import styled from "styled-components"
import { ClockCircleOutlined, BulbOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"

// Import Redux actions and selectors
import {
  fetchPracticeScores,
  fetchAptitudePracticeScores,
  fetchCompletedPracticeAssessments,
} from "../../../actions"

// Debug: Verify imports
console.log('🔍 PracticeTab imports:')
console.log('🔍 fetchPracticeScores:', typeof fetchPracticeScores)
console.log('🔍 fetchAptitudePracticeScores:', typeof fetchAptitudePracticeScores)
console.log('🔍 fetchCompletedPracticeAssessments:', typeof fetchCompletedPracticeAssessments)
import {
  selectPracticeScores,
  selectPracticeScoresLoading,  
  selectPracticeScoresError,
  selectAptitudePracticeScores2,
  selectAptitudePracticeScoresLoading,
  selectAptitudePracticeScoresError,
  selectCommunicationDayZero,
  selectAptitudeDayZero,
  selectCompletedPracticeAssessments,
  selectCompletedPracticeAssessmentsLoading,
  selectPracticeOnlyCommunicationCount,
  selectPracticeOnlyAptitudeCount,
  selectPracticeOnlyTechnicalCount,
  selectPracticeOnlyTotalHours,
} from "../../../selectors"

// Error Boundary Component
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart rendering error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <EmptyContainer>
          <div className="empty-title">Chart rendering error</div>
          <div className="empty-description">Unable to display progress chart. Please try refreshing the page.</div>
        </EmptyContainer>
      )
    }
    return this.props.children
  }
}

const { Title, Text } = Typography

// Styled Components with consistent design
const TabContent = styled.div`
  padding: 16px;
  min-height: 500px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 8px;
  }
`

const MetricCard = styled(Card)`
  border-radius: 8px;
  text-align: center;
  background: white;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 16px 12px;
    
    @media (max-width: 480px) {
      padding: 12px 8px;
    }
  }
`

const MetricIcon = styled.div`
  font-size: 20px;
  margin-bottom: 6px;
  color: ${(props) => props.color || "#1890ff"};
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 4px;
  }
`

const MetricValue = styled(Title)`
  margin: 0 !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #262626 !important;
  
  @media (max-width: 480px) {
    font-size: 16px !important;
  }
`

const MetricLabel = styled(Text)`
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`

const ChartSection = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`

const ChartHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const ChartTitle = styled(Title)`
  margin: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #333333 !important;
  
  @media (max-width: 480px) {
    font-size: 14px !important;
  }
`

const ChartContainer = styled.div`
  height: 350px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
  
  @media (max-width: 480px) {
    height: 250px;
  }
`

const ProgressSection = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  border: 1px solid #f0f0f0;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`

const ProgressTitle = styled(Title)`
  color: #333333 !important;
  font-size: 16px !important;
  margin-bottom: 16px !important;
  font-weight: 600 !important;
  
  @media (max-width: 480px) {
    font-size: 14px !important;
    margin-bottom: 12px !important;
  }
`

const ProgressStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const StatItem = styled.div`
  text-align: ${(props) => props.align || "left"};
`

const StatLabel = styled(Text)`
  font-size: 12px;
  color: #8c8c8c;
  display: block;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`

const StatValue = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.color || "#0FB680"};
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  flex-direction: column;
  gap: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`

const EmptyContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  
  @media (max-width: 480px) {
    padding: 30px 15px;
  }
`

const ErrorContainer = styled.div`
  margin-bottom: 16px;
`

// FIXED Activity Tracker Styled Components - RESPONSIVE FOR DRAWER
const ActivityTrackerCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    padding: 12px 16px;
    text-align: center;
    
    @media (max-width: 480px) {
      padding: 10px 12px;
    }
  }
  
  .ant-card-head-title {
    color: #262626 !important;
    margin-bottom: 0 !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    
    @media (max-width: 480px) {
      font-size: 14px !important;
    }
  }
  
  .ant-card-body {
    padding: 16px;
    background: #ffffff;
    
    @media (max-width: 480px) {
      padding: 12px;
    }
  }
`

const MonthNavigator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 480px) {
    gap: 12px;
    padding: 8px 12px;
  }
`

const MonthButton = styled(Button)`
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #595959;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  
  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
    background: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(64, 169, 255, 0.15);
  }
  
  &:focus {
    border-color: #40a9ff;
    color: #40a9ff;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  .anticon {
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    
    .anticon {
      font-size: 10px;
    }
  }
`

const MonthTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #262626;
  text-align: center;
  letter-spacing: 0.3px;
  flex: 1;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`

// FIXED: Responsive layout for drawer - stacks vertically on narrow screens
const ActivityTrackerLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  
  /* For wider drawers (>600px), show side by side */
  @media (min-width: 600px) {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 16px;
  }
  
  /* For very wide drawers (>800px), give more space to summary */
  @media (min-width: 800px) {
    grid-template-columns: 1fr 320px;
    gap: 20px;
  }
`

const CalendarPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const SummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

// FIXED: Responsive calendar container
const CalendarContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e9ecef;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.02);
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`

const WeekdaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 12px;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 2px;
    margin-bottom: 10px;
  }
`

const WeekdayLabel = styled.div`
  font-size: 10px;
  color: #8c8c8c;
  font-weight: 700;
  text-align: center;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 3px;
  border: 1px solid #e9ecef;
  
  @media (max-width: 480px) {
    font-size: 9px;
    height: 16px;
  }
`

const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 16px;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 2px;
    margin-bottom: 12px;
  }
`

// FIXED: Responsive activity day cells
const ActivityDay = styled.div`
  aspect-ratio: 1;
  width: 100%;
  min-width: 28px;
  max-width: 40px;
  border-radius: 6px;
  border: 2px solid ${(props) => {
    if (props.selected) return "#1890ff"
    if (props.active) return "#52c41a"
    return "#e8e8e8"
  }};
  background: ${(props) => {
    if (props.selected) {
      return "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)"
    }
    if (props.active) {
      return "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
    }
    return "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
  }};
  color: ${(props) => {
    if (props.selected) return "#1890ff"
    if (props.active) return "#389e0d"
    return "#bfbfbf"
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: ${(props) => (props.active || props.selected ? "700" : "500")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${(props) => (props.children ? "pointer" : "default")};
  position: relative;
  box-shadow: ${(props) => {
    if (props.selected) return "0 2px 6px rgba(24, 144, 255, 0.2)"
    if (props.active) return "0 2px 6px rgba(82, 196, 26, 0.2)"
    return "0 1px 2px rgba(0, 0, 0, 0.06)"
  }};
  
  &:hover {
    ${(props) =>
      props.children &&
      `
      transform: translateY(-1px) scale(1.05);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
      z-index: 1;
    `}
  }
  
  @media (max-width: 480px) {
    min-width: 24px;
    max-width: 32px;
    font-size: 10px;
    border-radius: 4px;
  }
`

const TrophyIcon = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  font-size: 8px;
  color: #faad14;
  
  .anticon {
    filter: drop-shadow(0 1px 2px rgba(250, 173, 20, 0.4));
  }
  
  @media (max-width: 480px) {
    font-size: 7px;
    top: 0px;
    right: 0px;
  }
`

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 6px;
  border: 1px solid #e9ecef;
  font-size: 11px;
  color: #595959;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 480px) {
    gap: 12px;
    padding: 8px 10px;
    font-size: 10px;
  }
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  
  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    border: 2px solid #52c41a;
    background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
    box-shadow: 0 1px 2px rgba(82, 196, 26, 0.2);
    
    @media (max-width: 480px) {
      width: 8px;
      height: 8px;
    }
  }
  
  .legend-trophy {
    color: #faad14;
    font-size: 12px;
    filter: drop-shadow(0 1px 2px rgba(250, 173, 20, 0.4));
    
    @media (max-width: 480px) {
      font-size: 10px;
    }
  }
`

// FIXED: Responsive summary card
const ActivitySummaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  overflow: hidden;
  width: 100%;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`

const SummaryHeader = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-bottom: 1px solid #e8e8e8;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`

const SummaryDate = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #262626;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  letter-spacing: 0.2px;
  
  @media (max-width: 480px) {
    font-size: 13px;
  }
`

const SummaryContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
  }
`

const ActivityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
    transform: translateX(2px);
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
  }
`

const ActivityLabel = styled.span`
  font-size: 12px;
  color: #595959;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`

const ActivityValue = styled.span`
  font-size: 13px;
  color: #262626;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 3px 6px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  min-width: 24px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  
  @media (max-width: 480px) {
    font-size: 12px;
    padding: 2px 5px;
    min-width: 20px;
  }
`

const AchievementBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #fff7e6 0%, #ffecc7 100%);
  border: 2px solid #faad14;
  border-radius: 8px;
  padding: 10px;
  margin-top: 6px;
  box-shadow: 0 3px 8px rgba(250, 173, 20, 0.15);
  
  .anticon {
    color: #faad14;
    font-size: 14px;
    filter: drop-shadow(0 1px 2px rgba(250, 173, 20, 0.3));
  }
  
  span {
    font-size: 12px;
    font-weight: 700;
    color: #d48806;
    letter-spacing: 0.2px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    gap: 6px;
    margin-top: 4px;
    
    .anticon {
      font-size: 12px;
    }
    
    span {
      font-size: 11px;
    }
  }
`

const NoActivityMessage = styled.div`
  text-align: center;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 500;
  padding: 20px 12px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 8px;
  border: 2px dashed #d9d9d9;
  margin: 6px 0;
  
  @media (max-width: 480px) {
    font-size: 11px;
    padding: 16px 10px;
    margin: 4px 0;
  }
`

// FIXED: Loading and Error wrappers for Activity Tracker
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  
  .loading-text {
    color: #8c8c8c;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 30px 15px;
    
    .loading-text {
      font-size: 13px;
    }
  }
`

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  text-align: center;
  
  .error-title {
    font-size: 16px;
    font-weight: 600;
    color: #ff4d4f;
    margin-bottom: 4px;
  }
  
  .error-description {
    font-size: 14px;
    color: #8c8c8c;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 30px 15px;
    
    .error-title {
      font-size: 14px;
    }
    
    .error-description {
      font-size: 13px;
    }
  }
`

const PracticeTab = ({
  practiceSubTab,
  setPracticeSubTab,
  renderActivityTracker,
  studentId, // Add studentId prop for data fetching
  studentEmail, // Add studentEmail prop for practice scores
  // FIXED: Activity Tracker props
  student,
  activityData = [],
  activityLoading = false,
  activityError = null,
  onActivityRetry = () => {},
}) => {
  console.log('🏗️ PracticeTab component rendered!')
  console.log('🏗️ Props received:', { studentId, studentEmail, student,activityData, activityLoading, activityError })
  
  const dispatch = useDispatch()

  // Redux selectors for practice data
  const practiceScores = useSelector(selectPracticeScores)
  const practiceScoresLoading = useSelector(selectPracticeScoresLoading)
  const practiceScoresError = useSelector(selectPracticeScoresError)
  const aptitudePracticeScores = useSelector(selectAptitudePracticeScores2)
  const aptitudePracticeScoresLoading = useSelector(selectAptitudePracticeScoresLoading)
  const aptitudePracticeScoresError = useSelector(selectAptitudePracticeScoresError)
  
  // DayZero selectors
  const communicationDayZero = useSelector(selectCommunicationDayZero)
  const aptitudeDayZero = useSelector(selectAptitudeDayZero)
  
  // Completed practice assessments selectors
  const completedPracticeAssessments = useSelector(selectCompletedPracticeAssessments)
  const completedPracticeAssessmentsLoading = useSelector(selectCompletedPracticeAssessmentsLoading)
  
  // Practice counts and hours
  const communicationAssessmentCount = useSelector(selectPracticeOnlyCommunicationCount)
  const aptitudeAssessmentCount = useSelector(selectPracticeOnlyAptitudeCount)
  const technicalAssessmentCount = useSelector(selectPracticeOnlyTechnicalCount)
  const totalPracticeHours = useSelector(selectPracticeOnlyTotalHours)

  // Format practice hours for display
  const formattedTotalPracticeHours = `${totalPracticeHours} hrs`

  // Generate chart data function - Use original API data
  const generateChartData = useCallback(() => {
    // Add proper null checks and ensure arrays for real data
    const communicationScores = Array.isArray(practiceScores?.data) ? practiceScores.data : 
                                 Array.isArray(practiceScores) ? practiceScores : []
    const aptitudeScores = Array.isArray(aptitudePracticeScores?.data) ? aptitudePracticeScores.data : 
                           Array.isArray(aptitudePracticeScores) ? aptitudePracticeScores : []

    // Create a map of all unique dates with their respective scores
    const dateScoresMap = new Map()

    // Add communication scores by date
    communicationScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.communicationScore = scoreData.total_score || 0
        entry.hasCommunication = true
        entry.communicationDelta = scoreData.delta || 0
      }
    })

    // Add aptitude scores by date
    aptitudeScores.forEach((scoreData) => {
      if (scoreData && scoreData.submittedAt) {
        const date = scoreData.submittedAt
        if (!dateScoresMap.has(date)) {
          dateScoresMap.set(date, {
            date,
            communicationScore: 0,
            aptitudeScore: 0,
            hasCommunication: false,
            hasAptitude: false
          })
        }
        const entry = dateScoresMap.get(date)
        entry.aptitudeScore = scoreData.total_score || 0
        entry.hasAptitude = true
        entry.aptitudeDelta = scoreData.delta || 0
      }
    })

    // Sort by date and create sessions
    const sortedDates = Array.from(dateScoresMap.values()).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    )

    const sessions = []

    // Start with session 0 (baseline with dayZero data)
    sessions.push({
      session: 0,
      communication_combined_progress: communicationDayZero?.total_score || 0,
      aptitude_combined_progress: aptitudeDayZero?.total_score || 0,
      submittedAt: null,
      assessmentAssignedId: null,
      delta: 0,
      type: 'dayZero'
    })

    console.log('🎯 Generated session 0 (dayZero baseline):', {
      session: 0,
      communication_combined_progress: communicationDayZero?.total_score || 0,
      aptitude_combined_progress: aptitudeDayZero?.total_score || 0,
      type: 'dayZero'
    })

    // Process each unique date - directly use backend scores
    sortedDates.forEach((dateEntry, index) => {
      // Create session entry with direct scores from backend
      sessions.push({
        session: index + 1,
        communication_combined_progress: dateEntry.hasCommunication ? dateEntry.communicationScore : 
                                        (sessions[sessions.length - 1]?.communication_combined_progress || 0),
        aptitude_combined_progress: dateEntry.hasAptitude ? dateEntry.aptitudeScore : 
                                   (sessions[sessions.length - 1]?.aptitude_combined_progress || 0),
        submittedAt: dateEntry.date,
        assessmentAssignedId: null,
        type: dateEntry.hasCommunication && dateEntry.hasAptitude ? 'both' : 
              dateEntry.hasCommunication ? 'communication' : 'aptitude',
        delta: dateEntry.hasCommunication ? dateEntry.communicationDelta : 
               dateEntry.hasAptitude ? dateEntry.aptitudeDelta : 0
      })
    })

    return sessions
  }, [practiceScores, aptitudePracticeScores, communicationDayZero, aptitudeDayZero])

  // Prepare chart data for rendering
  const prepareChartData = useCallback(() => {
    try {
      const chartData = generateChartData()
      return chartData.map((session) => ({
        session: session.session,
        "Communication Progress": session.communication_combined_progress || 0,
        "Aptitude Progress": session.aptitude_combined_progress || 0,
      }))
    } catch (error) {
      console.error("Error preparing chart data:", error)
      return []
    }
  }, [generateChartData])

  // Enhanced custom tooltip component (same as Overview.js)
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <p
            style={{
              fontWeight: "600",
              margin: "0 0 8px 0",
              color: "#262626",
              fontSize: "14px",
            }}
          >
            {`Session ${label}`}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                margin: "4px 0",
                color: entry.color,
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  // Fetch practice data on component mount
  useEffect(() => {
    console.log('🚀 PracticeTab useEffect triggered!')
    console.log('🚀 studentEmail:', student.email)
    console.log('🚀 dispatch:', typeof dispatch)
    
    dispatch(fetchPracticeScores(student.email)).catch((error) => {
      console.log("Failed to load practice scores:", error)
    })
    dispatch(fetchAptitudePracticeScores(student.email)).catch((error) => {
      console.log("Failed to load aptitude practice scores:", error)
    })
   
  }, [studentEmail, dispatch])

  // Debug dayZero data
  useEffect(() => {
    console.log('🎯 DayZero Debug - communicationDayZero:', communicationDayZero)
    console.log('🎯 DayZero Debug - aptitudeDayZero:', aptitudeDayZero)
    console.log('🎯 DayZero Debug - communicationDayZero.total_score:', communicationDayZero?.total_score)
    console.log('🎯 DayZero Debug - aptitudeDayZero.total_score:', aptitudeDayZero?.total_score)
  }, [communicationDayZero, aptitudeDayZero])

  // Fetch completed practice assessments
  useEffect(() => {
    if (studentId) {
      console.log('🔄 PracticeTab: Fetching completed practice assessments for studentId:', studentId)
      
      dispatch(fetchCompletedPracticeAssessments(studentId, true))
        .catch((error) => {
          console.error("Failed to fetch completed practice assessments:", error)
        })
    } else {
      console.warn('⚠️ PracticeTab: No studentId provided for completed assessments fetch')
    }
  }, [studentId, dispatch])

  // Get practice data and calculate final progress
  const practiceData = generateChartData()
  const finalCommunicationProgress = practiceData.length > 0 ? 
    practiceData[practiceData.length - 1]?.communication_combined_progress || 0 : 0
  const finalAptitudeProgress = practiceData.length > 0 ? 
    practiceData[practiceData.length - 1]?.aptitude_combined_progress || 0 : 0
  const finalCombinedProgress = (finalCommunicationProgress + finalAptitudeProgress) / 2

  // Check loading and error states
  const isLoading = practiceScoresLoading || aptitudePracticeScoresLoading || completedPracticeAssessmentsLoading
  const hasError = practiceScoresError || aptitudePracticeScoresError
  const errorMessage = practiceScoresError || aptitudePracticeScoresError

  // Check if components should show chart data
  const hasChartData = generateChartData().length > 0

  // Activity Tracker State
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(new Date().getDate())
  const [selectedDateData, setSelectedDateData] = useState(null)
  const [retryLoading, setRetryLoading] = useState(false)

  // Format activity data for calendar display
  const formattedActivityData = React.useMemo(() => {
    // Create a map of active dates with full data
    const activeDatesMap = {}
    activityData.forEach((item) => {
      activeDatesMap[item.Date] = {
        isActive: true,
        hasAchievement: item.is_trophy,
        cefr: item.Cefr,
        typeCounts: item.typeCounts || {
          communication: 0,
          aptitude: 0,
          behavior: 0,
          technical: 0,
        },
      }
    })

    // Generate calendar grid (42 days to fill 6 weeks)
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()

    // Get first day of month and calculate starting position
    const firstDay = new Date(currentYear, currentMonth, 1)
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1 // Monday = 0

    // Get number of days in current month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

    // Fill the grid with 42 days (6 rows × 7 days)
    const formattedData = []
    for (let i = 0; i < 42; i++) {
      if (i < startingDayOfWeek) {
        // Empty cells before month starts
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: { communication: 0, aptitude: 0, behavior: 0, technical: 0 },
        })
      } else if (i - startingDayOfWeek + 1 <= daysInMonth) {
        // Days of current month
        const day = i - startingDayOfWeek + 1
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
        const activityInfo = activeDatesMap[dateStr] || {
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: { communication: 0, aptitude: 0, behavior: 0, technical: 0 },
        }

        formattedData.push({
          day,
          ...activityInfo,
        })
      } else {
        // Empty cells after month ends
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: { communication: 0, aptitude: 0, behavior: 0, technical: 0 },
        })
      }
    }

    return formattedData
  }, [activityData, currentDate])

  // Handle date selection
  const handleDateSelect = (cell) => {
    if (cell.day) {
      setSelectedDate(cell.day)
      // Create selected date data object using real API data
      const dateData = {
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), cell.day),
        isActive: cell.isActive,
        hasAchievement: cell.hasAchievement,
        cefr: cell.cefr,
        // Use actual API data
        communicationCount: cell.typeCounts?.communication || 0,
        aptitudeCount: cell.typeCounts?.aptitude || 0,
        behaviorCount: cell.typeCounts?.behavior || 0,
      }

      setSelectedDateData(dateData)
    }
  }

  // Format selected date for display
  const formatSelectedDate = (date) => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  // Get month name
  const getMonthName = (date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  }

  // FIXED: Get current month's date range
  const getCurrentMonthRange = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const startDate = new Date(year, month, 1).toISOString()
    const endDate = new Date(year, month + 1, 0, 23, 59, 59).toISOString()
    return { startDate, endDate }
  }

  // FIXED: Navigate months with proper data fetching
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(currentDate.getMonth() + direction)
    setCurrentDate(newDate)
    
    // Fetch data for new month
    const { startDate, endDate } = getCurrentMonthRange(newDate)
    if (onActivityRetry) {
      onActivityRetry(newDate, startDate, endDate)
    }
  }

  // FIXED: Handle retry with proper date range
  const handleRetry = async () => {
    if (retryLoading) return
    setRetryLoading(true)
    try {
      const { startDate, endDate } = getCurrentMonthRange(currentDate)
      await onActivityRetry(currentDate, startDate, endDate)
    } finally {
      setRetryLoading(false)
    }
  }

  // FIXED: Initialize with today's data when activityData changes
  useEffect(() => {
    if (formattedActivityData.length > 0) {
      const today = new Date().getDate()
      const todayCell = formattedActivityData.find((cell) => cell.day === today)
      if (todayCell) {
        handleDateSelect(todayCell)
      }
    }
  }, [formattedActivityData])

  // FIXED: Add debug logging to check if data is being received
  useEffect(() => {
    console.log('PracticeTab received activityData:', activityData)
    console.log('PracticeTab activityLoading:', activityLoading)
    console.log('PracticeTab activityError:', activityError)
  }, [activityData, activityLoading, activityError])

  // Activity Day Cell Component
  const ActivityDayCell = ({ day, isActive, hasAchievement, cefr, isSelected, onClick }) => (
    <ActivityDay
      active={isActive && day}
      selected={isSelected}
      onClick={onClick}
      title={day ? `Day ${day}${cefr ? ` - ${cefr}` : ""}` : ""}
    >
      {day ? (
        <>
          {day}
          {hasAchievement && isActive && (
            <TrophyIcon>
              <TrophyOutlined />
            </TrophyIcon>
          )}
        </>
      ) : (
        ""
      )}
    </ActivityDay>
  )

  // Render Activity Tracker Component - FIXED RESPONSIVE LAYOUT
  const renderActivityTrackerComponent = () => (
    <ActivityTrackerCard bordered={false} title="Activity Tracker">
      <MonthNavigator>
        <MonthButton icon={<LeftOutlined />} onClick={() => navigateMonth(-1)} size="small" />
        <MonthTitle>{getMonthName(currentDate)}</MonthTitle>
        <MonthButton icon={<RightOutlined />} onClick={() => navigateMonth(1)} size="small" />
      </MonthNavigator>

      {activityLoading ? (
        <LoadingWrapper>
          <Spin size="large" />
          <span className="loading-text">Loading activity data...</span>
        </LoadingWrapper>
      ) : activityError ? (
        <ErrorWrapper>
          <div className="error-title">Failed to load activity data</div>
          <div className="error-description">{activityError}</div>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry} loading={retryLoading}>
            Retry
          </Button>
        </ErrorWrapper>
      ) : (
        <ActivityTrackerLayout>
          {/* Calendar Panel */}
          <CalendarPanel>
            <CalendarContainer>
              <WeekdaysGrid>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <WeekdayLabel key={day}>{day}</WeekdayLabel>
                ))}
              </WeekdaysGrid>

              <ActivityGrid>
                {formattedActivityData.map((cell, idx) => (
                  <ActivityDayCell
                    key={idx}
                    day={cell.day}
                    isActive={cell.isActive}
                    hasAchievement={cell.hasAchievement}
                    cefr={cell.cefr}
                    isSelected={selectedDate === cell.day && cell.day !== null}
                    onClick={() => handleDateSelect(cell)}
                  />
                ))}
              </ActivityGrid>

              <LegendContainer>
                <LegendItem>
                  <span className="legend-dot"></span>
                  <span>Active days</span>
                </LegendItem>
                <LegendItem>
                  <TrophyOutlined className="legend-trophy" />
                  <span>Achievement unlocked</span>
                </LegendItem>
              </LegendContainer>
            </CalendarContainer>
          </CalendarPanel>

          {/* Summary Panel */}
          <SummaryPanel>
            <ActivitySummaryCard>
              <SummaryHeader>
                <SummaryDate>
                  {selectedDateData ? formatSelectedDate(selectedDateData.date) : formatSelectedDate(new Date())}
                </SummaryDate>
              </SummaryHeader>

              <SummaryContent>
                {selectedDateData && selectedDateData.isActive ? (
                  <>
                    <ActivityItem>
                      <ActivityLabel>Practiced Communication:</ActivityLabel>
                      <ActivityValue>{selectedDateData.communicationCount || 0}</ActivityValue>
                    </ActivityItem>
                    <ActivityItem>
                      <ActivityLabel>Practiced Aptitude:</ActivityLabel>
                      <ActivityValue>{selectedDateData.aptitudeCount || 0}</ActivityValue>
                    </ActivityItem>
                    <ActivityItem>
                      <ActivityLabel>Practiced Behavior:</ActivityLabel>
                      <ActivityValue>{selectedDateData.behaviorCount || 0}</ActivityValue>
                    </ActivityItem>
                    {selectedDateData.hasAchievement && (
                      <AchievementBadge>
                        <TrophyOutlined />
                        <span>Communication - Level Up!</span>
                      </AchievementBadge>
                    )}
                  </>
                ) : (
                  <NoActivityMessage>No practice activity recorded for this date</NoActivityMessage>
                )}
              </SummaryContent>
            </ActivitySummaryCard>
          </SummaryPanel>
        </ActivityTrackerLayout>
      )}
    </ActivityTrackerCard>
  )

  const getMetricColor = (type) => {
    switch (type) {
      case "hours":
        return "#3249D7"
      case "communication":
        return "#0FB680"
      case "aptitude":
        return "#faad14"
      case "technical":
        return "#722ed1"
      default:
        return "#3249D7"
    }
  }

  const renderOverviewMetrics = () => (
    <MetricsGrid>
      <MetricCard>
        <MetricIcon color={getMetricColor("hours")}>
          <ClockCircleOutlined />
        </MetricIcon>
        {completedPracticeAssessmentsLoading ? (
          <Spin size="small" />
        ) : (
          <MetricValue>{formattedTotalPracticeHours}</MetricValue>
        )}
        <MetricLabel>Total Hours</MetricLabel>
      </MetricCard>
      <MetricCard>
        <MetricIcon color={getMetricColor("communication")}>
          <TrophyOutlined />
        </MetricIcon>
        {completedPracticeAssessmentsLoading ? (
          <Spin size="small" />
        ) : (
          <MetricValue>{communicationAssessmentCount}</MetricValue>
        )}
        <MetricLabel>Communication</MetricLabel>
      </MetricCard>
      <MetricCard>
        <MetricIcon color={getMetricColor("aptitude")}>
          <BulbOutlined />
        </MetricIcon>
        {completedPracticeAssessmentsLoading ? (
          <Spin size="small" />
        ) : (
          <MetricValue>{aptitudeAssessmentCount}</MetricValue>
        )}
        <MetricLabel>Aptitude</MetricLabel>
      </MetricCard>
      <MetricCard>
        <MetricIcon color={getMetricColor("technical")}>
          <BulbOutlined />
        </MetricIcon>
        {completedPracticeAssessmentsLoading ? (
          <Spin size="small" />
        ) : (
          <MetricValue>{technicalAssessmentCount}</MetricValue>
        )}
        <MetricLabel>Technical</MetricLabel>
      </MetricCard>
    </MetricsGrid>
  )

  const preparePracticeChartData = () => {
    return prepareChartData()
  }

  return (
    <TabContent>
      {renderOverviewMetrics()}

      {/* FIXED: Render the integrated Activity Tracker with proper data */}
      {renderActivityTrackerComponent()}

      <ChartSection>
        <ChartHeader>
          <ChartTitle>Practice Progress Analytics</ChartTitle>
        </ChartHeader>

        {isLoading ? (
          <LoadingContainer>
            <Spin size="large" />
            <Text style={{ color: "#8c8c8c" }}>Loading your progress data...</Text>
          </LoadingContainer>
        ) : hasError ? (
          <ErrorContainer>
            <Alert
              message="Error Loading Data"
              description={`Failed to load progress data: ${errorMessage}`}
              type="error"
              showIcon
            />
          </ErrorContainer>
        ) : !hasChartData ? (
          <EmptyContainer>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No practice data available yet. Complete some practice assessments to see your progress!"
            />
          </EmptyContainer>
        ) : (
          <>
            <ChartErrorBoundary>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={preparePracticeChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="session"
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      tickCount={Math.min(generateChartData().length, 10)}
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                      label={{ 
                        value: 'Practice Sessions', 
                        position: 'insideBottom', 
                        offset: -10, 
                        style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 'bold' } 
                      }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                      label={{ 
                        value: 'Scores (%)', 
                        angle: -90, 
                        position: 'insideLeft', 
                        style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 'bold' } 
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="line"
                      wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                    />
                    {/* Communication Progress Line */}
                    <Line
                      type="monotone"
                      dataKey="Communication Progress"
                      stroke="#52c41a"
                      strokeWidth={3}
                      dot={{ fill: "#52c41a", strokeWidth: 2, r: 4 }}
                      activeDot={{
                        r: 6,
                        fill: "#52c41a",
                        stroke: "#ffffff",
                        strokeWidth: 2,
                      }}
                      name="Communication Progress"
                      connectNulls={false}
                    />
                    {/* Aptitude Progress Line */}
                    <Line
                      type="monotone"
                      dataKey="Aptitude Progress"
                      stroke="#1890ff"
                      strokeWidth={3}
                      dot={{ fill: "#1890ff", strokeWidth: 2, r: 4 }}
                      activeDot={{
                        r: 6,
                        fill: "#1890ff",
                        stroke: "#ffffff",
                        strokeWidth: 2,
                      }}
                      name="Aptitude Progress"
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </ChartErrorBoundary>

            {/* <ProgressSection>
              <ProgressTitle>Overall Progress</ProgressTitle>
              <Progress
                percent={Math.round(finalCombinedProgress)}
                strokeColor={{ "0%": "#52c41a", "100%": "#73d13d" }}
                strokeWidth={8}
                showInfo={false}
                style={{ marginBottom: 16 }}
              />
              <ProgressStats>
                <StatItem align="left">
                  <StatLabel>Current Level</StatLabel>
                  <StatValue color="#0FB680">{Math.round(finalCombinedProgress)}%</StatValue>
                </StatItem>
                <StatItem align="right">
                  <StatLabel>Sessions Completed</StatLabel>
                  <StatValue color="#1890ff">{practiceData.length}</StatValue>
                </StatItem>
              </ProgressStats>
            </ProgressSection> */}
          </>
        )}
      </ChartSection>
    </TabContent>
  )
}

export default PracticeTab
